import React, { useCallback, useRef, useState } from 'react'
import { Badge } from '../../Badge'
import { useDragReorder } from '../../../hooks'
import { arrayMove } from '../../../utils/arrayMove'
import MarkdownEditor from '../../MarkdownEditor/MarkdownEditor'
import formStyles from '../../../styles/forms.module.scss'
import styles from './FaqEditor.module.scss'

// Stored metadata shape — mirrors what ACE's hydrateFaq parses
// (apps/ace/internal/service/layout_db.go) and tc.widget.faq.v1. The question
// is plain text (not markdown — schema.org/FAQPage markup wants it clean); the
// answer is block markdown with no raw HTML passthrough on clients. A widget
// is one flat group of Q/A pairs: multiple categories means multiple FAQ
// widgets, each carrying its category as `title` (surfaced as
// Widget.meta.title). Items deliberately carry NO ids — clients key the
// accordion by position — so the React keys below are transient, per-session
// only, and never saved. Spec: docs/html/tennischannel/widgets/faq.html
export interface FaqItemMetadata {
  question?: string
  answer?: string
}

export interface FaqMetadata {
  title?: string
  items?: FaqItemMetadata[]
}

interface FaqEditorProps {
  metadata: FaqMetadata
  onChange: (metadata: FaqMetadata) => void
}

const FaqEditor: React.FC<FaqEditorProps> = ({ metadata, onChange }) => {
  const items = metadata.items ?? []

  // Latest metadata for callbacks that would otherwise close over a stale
  // render's items (the uncontrolled MarkdownEditor keeps firing onChange
  // across reorders).
  const metadataRef = useRef(metadata)
  metadataRef.current = metadata

  // Transient row identities for React keys, drag reorder, and the expanded
  // set. Parallel to items; extended here so externally added rows (e.g. a
  // metadata reload) get keys too. Never persisted — see the shape note above.
  const idsRef = useRef<string[]>([])
  while (idsRef.current.length < items.length) idsRef.current.push(crypto.randomUUID())
  if (idsRef.current.length > items.length) idsRef.current.length = items.length

  // Which items are expanded for editing. Collapsed rows are compact and
  // draggable; a newly added item opens expanded so it can be filled in.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // metadataRef is also assigned here, synchronously, not just at render:
  // idsRef mutates eagerly inside reorder/remove callbacks, and a MarkdownEditor
  // onChange landing before the next render would otherwise index the NEW ids
  // against the OLD items array and misroute the edit (or resurrect a removed row).
  const update = useCallback(
    (patch: Partial<FaqMetadata>) => {
      const next = { ...metadataRef.current, ...patch }
      metadataRef.current = next
      onChange(next)
    },
    [onChange],
  )

  const commitItems = useCallback(
    (next: FaqItemMetadata[]) => update({ items: next }),
    [update],
  )

  // Patch an item by transient id off the latest metadata, so an edit landing
  // after a reorder can't misroute to whatever row now sits at the old index.
  const patchItemById = useCallback(
    (id: string, patch: Partial<FaqItemMetadata>) => {
      const index = idsRef.current.indexOf(id)
      if (index === -1) return
      const cur = metadataRef.current.items ?? []
      commitItems(cur.map((item, i) => (i === index ? { ...item, ...patch } : item)))
    },
    [commitItems],
  )

  const { dragIndex, handleProps, rowProps } = useDragReorder(
    useCallback(
      (from: number, to: number) => {
        idsRef.current = arrayMove(idsRef.current, from, to)
        commitItems(arrayMove(metadataRef.current.items ?? [], from, to))
      },
      [commitItems],
    ),
    { dragImageFromParent: true },
  )

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const addItem = () => {
    const id = crypto.randomUUID()
    idsRef.current = [...idsRef.current, id]
    commitItems([...(metadataRef.current.items ?? []), {}])
    setExpanded(prev => new Set(prev).add(id))
  }

  const removeItem = (id: string) => {
    const index = idsRef.current.indexOf(id)
    if (index === -1) return
    idsRef.current = idsRef.current.filter(x => x !== id)
    commitItems((metadataRef.current.items ?? []).filter((_, i) => i !== index))
    setExpanded(prev => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  return (
    <>
      <div className={formStyles.fieldGroup}>
        <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
        <input
          type="text"
          value={metadata.title || ''}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Optional group heading, e.g. Billing"
        />
        <p className={formStyles.hint}>
          One FAQ widget is one flat group — for multiple categories, place multiple
          FAQ widgets, each titled with its category.
        </p>
      </div>

      <div className={styles.itemsHeader}>
        <span>Questions</span>
        <Badge variant="secondary" size="small">{items.length}</Badge>
      </div>

      {items.length === 0 && (
        <p className={formStyles.hint}>No questions yet — an empty FAQ renders nothing.</p>
      )}

      {items.map((item, i) => {
        const id = idsRef.current[i]
        const isOpen = expanded.has(id)
        // Mirrors tc.widget.faq.v1: an item needs a question and an answer.
        const missing = !item.question?.trim() ? 'question' : !item.answer?.trim() ? 'answer' : null
        return (
          <div
            key={id}
            className={`${styles.item} ${dragIndex === i ? styles.dragging : ''}`}
            {...rowProps(i)}
          >
            <div className={styles.itemRow}>
              <span
                className={styles.handle}
                title="Drag to reorder"
                aria-label="Drag to reorder"
                {...handleProps(i)}
              >
                ⠿
              </span>

              <button type="button" className={styles.summary} onClick={() => toggleExpanded(id)}>
                <span className={styles.summaryTitle}>
                  {item.question?.trim() || `Question ${i + 1}`}
                </span>
              </button>

              {missing && (
                <Badge variant="warning" size="small">Needs {missing}</Badge>
              )}

              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => toggleExpanded(id)}
                title={isOpen ? 'Collapse' : 'Edit'}
                aria-expanded={isOpen}
              >
                {isOpen ? '▲' : '▼'}
              </button>
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.remove}`}
                onClick={() => removeItem(id)}
                title="Remove question"
              >
                ✕
              </button>
            </div>

            {isOpen && (
              <div className={styles.itemBody}>
                <div className={formStyles.fieldGroup}>
                  <label>Question <Badge variant="secondary" size="small">Public</Badge></label>
                  <input
                    type="text"
                    value={item.question || ''}
                    onChange={(e) => patchItemById(id, { question: e.target.value })}
                    placeholder="How do I cancel my subscription?"
                  />
                  <p className={formStyles.hint}>
                    Plain text — the accordion header. Markdown won’t render here.
                  </p>
                </div>

                <div className={formStyles.fieldGroup}>
                  <label>Answer <Badge variant="secondary" size="small">Public</Badge></label>
                  <MarkdownEditor
                    initialValue={item.answer || ''}
                    onChange={(markdown) => patchItemById(id, { answer: markdown })}
                    placeholder="Write the answer…"
                    ariaLabel={`Answer to question ${i + 1}`}
                  />
                  <p className={formStyles.hint}>
                    Markdown — paragraphs, lists, and links. Raw HTML won’t render.
                  </p>
                </div>
              </div>
            )}
          </div>
        )
      })}

      <button type="button" onClick={addItem} className={styles.addItem}>
        + Add question
      </button>
    </>
  )
}

export default FaqEditor
